import { datadogRum } from '@datadog/browser-rum';
import type { UiEffectContext, WorkflowHistoryEntry } from 'flow/store/redux/types';
import { getActionType } from 'flow/service/publicIds';
import type { HistoryAction } from './constants';

const HISTORY_ACTION_NAME_PREFIX = 'workflow.history';

/** Datadog RUM custom action name, e.g. `workflow.history.undo`. */
export function getHistoryActionName(historyAction: HistoryAction) {
  return `${HISTORY_ACTION_NAME_PREFIX}.${historyAction}`;
}

/**
 * The node the change belongs to. A context can be anchored to the changed action itself
 * (`publicId`) or to the action that owns the change (`actionId`). Batch changes may have no
 * single action to anchor to, and version-level changes have no action at all.
 */
function getActionPublicId(uiEffectContext: UiEffectContext) {
  if ('actionId' in uiEffectContext) {
    return uiEffectContext.actionId;
  }
  return 'publicId' in uiEffectContext ? uiEffectContext.publicId : null;
}

/**
 * Reports a completed undo / redo to Datadog RUM.
 *  - 'actionType': the node the change belonged to (a matrix model, an output attribute, etc)
 *  - 'target': the kind of change (a threshold, a matrix value, a data route, etc)
 *  - 'operation': added, modified, or removed
 */
export function trackHistoryTraversal({
  entry,
  historyAction,
}: {
  entry: WorkflowHistoryEntry;
  historyAction: HistoryAction;
}) {
  const actionPublicId = getActionPublicId(entry.uiEffectContext);

  datadogRum.addAction(getHistoryActionName(historyAction), {
    actionType: actionPublicId ? getActionType(actionPublicId) : null,
    target: entry.uiEffectContext.target,
    operation: entry.operation,
  });
}
